import {type} from 'arktype';
import React, {useCallback, useEffect, useMemo, useReducer, useState} from 'react';
import {useImmerState} from '~/library/form-models.v1';
import {FormAction, FormState} from '~/library/form-models.v2';
import {useInHistoryStateNamespace} from '../lib/useInHistoryState';
import {NavigateToEntry, useNavigateFromEntry, useNavigationEntryPoint} from '../lib/useNavigateWithCount';
import {SendTxResources} from '../model/SendTxContainer';
import {SendTxResourcesContextProvider} from '../model/SendTxResourcesContext';
import {SendTrTxDirector, SendTxTravelRuleForm} from './SendTrTx';
import {SendTxFlowType, SendTxIntro, SendTxIntroForm, SendTxIntroResult, SendTxIntroValues} from './SendTxIntro';
import {SendUsualTxDirector, SendUsualTxForm} from './SendUsualTx';

export function SendTxDirector({
    resources,
    updateResources,
}: {
    resources: SendTxResources;
    updateResources: () => void;
}) {
    const entry = useNavigationEntryPoint();
    const navigate = useNavigateFromEntry(entry);
    const useInHistoryState = useInHistoryStateNamespace('send_dir', navigate);
    const [direction, setDirection] = useInHistoryState(
        useMemo(() => TopLevelDirection, []),
        {
            flow: SendTxFlowType.intro,
        }
    );

    const [introState, dispatchToIntroState] = useReducer(
        FormState.reduce<SendTxIntroValues>,
        undefined,
        SendTxIntroForm
    );
    const {update: updateIntroState} = FormAction.bindGeneralActions(dispatchToIntroState);

    const [introResult, setIntroResult] = useState<SendTxIntroResult>();

    const [trState, writeTrTxState] = useImmerState(SendTxTravelRuleForm);

    const [usualFrom, writeUsualTxState] = useImmerState(SendUsualTxForm);

    function onSubmitProblem(issues: {address?: string; amount?: string}): void {
        updateResources();
        updateIntroState(form => {
            form.submitIssues = issues;
        });
    }

    useEffect(() => {
        if (direction.flow === SendTxFlowType.intro) {
            updateResources();
        }
    }, [direction.flow]);

    let screen = <NavigateToEntry entry={entry} />;

    if (direction.flow === SendTxFlowType.intro) {
        screen = (
            <SendTxIntro
                formState={introState}
                dispatchToForm={dispatchToIntroState}
                onNextStep={result => {
                    setIntroResult(result);
                    setDirection({flow: result.flow});
                }}
            />
        );
    }
    if (introResult) {
        if (direction.flow === SendTxFlowType.travelRule) {
            screen = (
                <SendTrTxDirector
                    entry={entry}
                    intro={introResult}
                    setFormState={writeTrTxState}
                    formState={trState}
                    resources={resources}
                    onSubmitProblem={onSubmitProblem}
                />
            );
        }
        if (direction.flow === SendTxFlowType.usual) {
            screen = (
                <SendUsualTxDirector
                    entry={entry}
                    resources={resources}
                    intro={introResult}
                    setFormState={writeUsualTxState}
                    formState={usualFrom}
                    onSubmitProblem={onSubmitProblem}
                    updateResources={updateResources}
                />
            );
        }
    }

    return (
        <SendTxResourcesContextProvider value={useCallback(() => resources, [resources])}>
            {screen}
        </SendTxResourcesContextProvider>
    );
}

const TopLevelDirection = type.never
    .or({
        flow: type.unit(SendTxFlowType.intro),
    })
    .or({
        flow: type.unit(SendTxFlowType.travelRule),
    })
    .or({
        flow: type.unit(SendTxFlowType.usual),
    });
